/**
 * Simulated promo records + measurements for the demo store.
 *
 * Shapes match the real `GET /api_db/promos/` and `/promos/:id/measure/` payloads
 * (see docs/reference/promo_measurement.md), so porting the views to production is a
 * swap of the data source, not a rewrite.
 *
 * SCOPE — a promo is coupled to a ZONE. Two things are measured and we do not try to
 * go inside the zone:
 *
 *   reach · engagement   the ZONE's, from cameras, free every night
 *   units · margin       the PROMOTED ITEMS', from the one sales figure entered
 *
 * Every euro below was produced by running backend/applicaite/coach/promo.py on the
 * inputs in each record — not authored by hand. Re-derive them if an input changes.
 *
 * The three finished promos show three different lessons:
 *   1. Power Tools      — items nearly tripled and it STILL lost money: the discount
 *                         was too deep, and sales then sat below normal for weeks.
 *   2. Garden Utilities — no discount at all, so every extra sale kept full margin,
 *                         and the lift outlived the promo.
 *   3. Safety Shoes     — sold more, not enough to cover the money taken off, and
 *                         back to normal the week it ended.
 */

import { demoDateStr } from '@/api/demoData'
// Descriptive text carries its Dutch (withNl): see NL_TEXT at the bottom of the records.
import { withNl } from '@/demo/demoLang'

const ZONE = {
  power: 'Power Tools',
  garden: 'Garden Utilities',
  safety: 'Safety Shoes',
  promoted: 'Promoted Articles Zone',
}

/**
 * Dates are "days before the demo's today", resolved through demoDateStr() — the
 * same anchor the rest of the demo store rebases on. Hardcoded calendar dates would
 * drift out of the last-12-months window and contradict the other surfaces.
 */
const D = (daysAgo) => demoDateStr(daysAgo)

/** A flat daily series with deterministic wobble, so charts look real but never move. */
function series(startDaysAgo, days, base, wobble = 0.08) {
  const out = []
  for (let i = 0; i < days; i++) {
    const w = Math.sin(i * 1.7) * wobble
    out.push({ date: D(startDaysAgo - i), value: Math.round(base * (1 + w) * 10) / 10 })
  }
  return out
}

// `*Base` is the first day of the comparison window, and it is ALWAYS 28 days before
// the promo starts for a promo of 8 days or more — the same length as the after-period,
// because reading 2 weeks before against 4 weeks after is indefensible and is the first
// thing anyone asks about. Mirrors coach/promo._comparison_shape.
const T = {
  runStart: 3, runEnd: -10, runBase: 31,
  powStart: 87, powEnd: 74, powBase: 115,
  safStart: 111, safEnd: 102, safBase: 139,
  // a single weekend, long finished — the state almost every promo sits in: it ran,
  // the weeks after have passed, and nobody has put the sales figures in yet
  weekStart: 66, weekEnd: 65, weekBase: 94,
  garStart: 144, garEnd: 124, garBase: 172,
  // a single Saturday — the shape a store-wide customer day always has
  kdStart: 38, kdEnd: 38, kdBase: 66,
  // ENDED six days ago, with the four weeks after still running. The state every
  // promo passes through and the demo had none of: the reading against the weeks
  // before is finished, the tail is not.
  jfStart: 16, jfEnd: 6, jfBase: 44,
}

/** Declared at logging time; only ever drives a warning. See coach/promo.COVERAGE. */
const COVERAGE = {
  few_items: {
    label: 'a few items',
    footfallIsHardToRead: true,
    warning: 'A few items in a whole zone may not change how many people walk into it, '
      + 'even when they sell well — so the footfall numbers will be hard to pin on this '
      + 'promo either way. The money answer comes from the sales figures.',
  },
  section: {
    label: 'a shelf or brand block',
    footfallIsHardToRead: true,
    warning: 'A single shelf inside the zone may not move the zone\'s footfall much. '
      + 'Treat those numbers as background rather than proof.',
  },
  most_of_aisle: { label: 'most of the zone', footfallIsHardToRead: false, warning: null },
}
withNl(COVERAGE.few_items, {
  label: 'een paar artikelen',
  warning: 'Een paar artikelen in een hele zone veranderen misschien niet hoeveel mensen die zone in lopen, '
    + 'ook als ze goed verkopen — dus de bezoekerscijfers zijn hoe dan ook moeilijk aan deze promo toe '
    + 'te schrijven. Het antwoord over geld komt uit de verkoopcijfers.',
})
withNl(COVERAGE.section, {
  label: 'een schap of merkblok',
  warning: 'Eén schap in de zone verandert het aantal bezoekers van de zone misschien nauwelijks. '
    + 'Zie die cijfers als achtergrond, niet als bewijs.',
})
withNl(COVERAGE.most_of_aisle, { label: 'het grootste deel van de zone' })

// ---------------------------------------------------------------------------
// 0b. Just finished — the promo is over, the four weeks after are not.
//
//     A promo has a result the DAY IT ENDS: the comparison against the weeks before
//     is complete and the euro is already countable. What is not in yet is whether
//     the lift outlived the promo, so everything that speaks about "the 4 weeks
//     after" is withheld rather than read off the six days that have passed — an
//     after-effect taken from a quarter of its window is a false claim, not an early
//     one. Hence: no `perDay.after`, `decay.after` null, `margin.afterKnown` false,
//     `windows.post.ready` false, and confidence `early` rather than `good`.
// ---------------------------------------------------------------------------

const JUST_FINISHED = {
  PromoId: 7,
  CompanyId: 0,
  StartDate: D(T.jfStart),
  EndDate: D(T.jfEnd),
  Status: 'active',
  Data: {
    name: 'Winter boots −15%',
    zoneName: ZONE.safety,
    productScope: 'S3 winter-lined boots',
    items: [
      { name: 'S3 winter-lined boot', price: 85, marginPct: 40, discountPct: 15 },
    ],
    coverage: 'section',
    mechanic: 'pct_off',
    advertised: true,
    discountPct: 15,
    fixedCostEur: 180,
    // The before and during figures are in — they are knowable the day it ends. The
    // after row is not, and the form will not let anyone type it yet.
    salesPeriods: {
      before: { units: 112, revenue: 9520, cost: 5712 },
      during: { units: 93, revenue: 6719, cost: 4743 },
    },
    postWindowDays: 28,
    entryMode: 'retrospective',
    notes: null,
  },
  measurement: {
    promoId: 7,
    zone: ZONE.safety,
    phase: 'settling',
    advertised: true,
    capability: { scope: 'zone', geometry: 'polygon', engagementMeasurable: true,
                  conversionBase: 'engaged' },
    coverage: COVERAGE.section,
    windows: {
      baseline: { start: D(T.jfBase), end: D(T.jfStart + 1), mode: 'contiguous', days: 28 },
      window: { start: D(T.jfStart), end: D(T.jfEnd), days: 11 },
      // six of the twenty-eight days have passed, and `ready` is what every surface
      // reads rather than comparing the two itself
      post: { days: 6, target: 28, weeks: 4, ready: false, readyOn: D(-22) },
    },
    excludedDays: 0,
    excludedReasons: {},
    confoundingActions: [],
    storeTraffic: { beforePerDay: 905, duringPerDay: 921, deltaPct: 1.8,
                    mdePct: 12.6, beyondNormal: false },
    detectability: {
      camera: { mdePct: 19.4, metric: 'stops', isVerdict: true, nBaseline: 28, nWindow: 11 },
      sales: { mdePct: 24.0, isVerdict: true, nBaseline: 28, nWindow: 11 },
    },
    // no `after` key: the window it would describe is still running
    perDay: {
      before: { visitors: 94, engaged: 39, promoUnits: 4.0, takeRate: 0.1026 },
      during: { visitors: 112, engaged: 50, promoUnits: 8.45, takeRate: 0.169 },
    },
    promotedSales: {
      available: true,
      trendBasis: 'control_zones',
      trendRatio: 1.0,
      promoUnitsPerDay: { before: 4.0, after: 8.45, expected: 4.0 },
      takeRate: { before: 0.1026, after: 0.169, deltaPct: 64.7 },
    },
    decomposition: {
      outcome: 'revenue',
      totalPct: 79.6,
      dropped: [],
      terms: [
        { term: 'footfall', before: 905, after: 921, deltaPct: 1.8, contributionPts: 2.4 },
        { term: 'reach', before: 0.104, after: 0.122, deltaPct: 17.3, contributionPts: 21.8 },
        { term: 'engagement', before: 0.415, after: 0.446, deltaPct: 7.5, contributionPts: 9.9 },
        { term: 'conversion', before: 0.103, after: 0.169, deltaPct: 64.1, contributionPts: 67.7 },
        // a 15% price cut is money off every sale, so the basket term is NEGATIVE
        // even on a promo that worked — which is the whole reason the euro and the
        // unit count are read separately
        { term: 'basket', before: 85, after: 72.25, deltaPct: -15.0, contributionPts: -22.2 },
      ],
    },
    decay: { during: 'sold_more', after: null, archetype: null, basis: 'sales',
             liftW: 1.114, liftP: null, band: 0.24 },
    economics: {
      basis: 'your_items', items: 1, avgPrice: 85,
      unitMarginNormal: 34, unitMarginOnPromo: 21.25,
      discountPctMin: 15, discountPctMax: 15, uniformDiscount: true,
    },
    margin: {
      duringEur: 300,
      // not 0 and not a guess: the window has not closed
      afterEur: null,
      afterKnown: false,
      eur: 300,
      priceBasis: 'your_items',
      itemCount: 1,
      price: 85,
      unitMarginNormal: 34,
      unitMarginOnPromo: 21.25,
      discountPctMin: 15,
      discountPctMax: 15,
      uniformDiscount: true,
      breakEvenLift: 0.6,
      achievedLift: 1.114,
      clearedBreakEven: true,
      impossible: false,
      fixedCostEur: 180,
      basis: 'till',
      // `early`, not `good`: the during euro is real, the total is not final. It is
      // what keeps this promo out of the cumulative "made by your promotions".
      confidence: 'early',
    },
    weatherCaveat: null,
    daily: {
      engaged: [
        ...series(T.jfBase, 28, 39),
        ...series(T.jfStart, 11, 50),
        ...series(T.jfEnd - 1, 6, 44),
      ],
    },
  },
}

// ---------------------------------------------------------------------------
// 0. The whole store — "Dag van de klant", a gift for every customer.
//    Not attached to any shelf, so it is measured on store footfall and the till,
//    and falls into the classic retail identity instead of the zone funnel.
// ---------------------------------------------------------------------------

const CUSTOMER_DAY = {
  PromoId: 6,
  CompanyId: 0,
  StartDate: D(T.kdStart),
  EndDate: D(T.kdEnd),
  Status: 'concluded',
  Data: {
    name: 'Dag van de klant',
    // the store IS the scope: no zone, no coverage, no item list
    scope: 'store',
    zoneName: null,
    productScope: 'A small gift for every customer',
    mechanic: 'gift',
    advertised: true,
    discountPct: 0,
    // 1,500 gifts, bought up front. You never know the turnout, so this is what you
    // SPENT, not what you handed out — a sunk set-up cost like any other.
    fixedCostEur: 1800,
    // No cost column store-wide: a whole shop's cost of goods for four particular
    // Saturdays is not a figure a retailer has to hand. One average margin instead,
    // typed once — the euro below is modelled from it and says so. (38% of each of
    // these takings is exactly the gross the cost figures used to give.)
    storeMarginPct: 38,
    salesPeriods: {
      before: { units: 856, revenue: 26878 },
      during: { units: 498, revenue: 14392 },
      after: { units: 884, revenue: 27492 },
    },
    postWindowDays: 28,
    entryMode: 'retrospective',
    outcome: { repeat: 'as_was', note: 'Queue at the till was the only problem.' },
  },
  measurement: {
    promoId: 6,
    zone: 'Whole store',
    phase: 'complete',
    advertised: true,
    // the store is the scope, so there is no zone funnel and no coverage question
    capability: { scope: 'store', geometry: 'store', engagementMeasurable: false,
                  conversionBase: 'footfall' },
    coverage: null,
    windows: {
      // one day -> the four comparable Saturdays either side of it
      baseline: { start: D(T.kdBase), end: D(T.kdStart + 7), mode: 'matched-weekday', days: 4 },
      window: { start: D(T.kdStart), end: D(T.kdEnd), days: 1 },
      post: { days: 4, target: 28, weeks: 4 },
    },
    excludedDays: 0,
    excludedReasons: {},
    confoundingActions: [],
        detectability: {
      // a single day against four Saturdays is a WIDE band — and a customer day
      // moves footfall so hard that it clears it anyway
      camera: { mdePct: 22.4, metric: 'store_traffic', isVerdict: true,
                nBaseline: 4, nWindow: 1 },
      sales: { mdePct: 25.1, isVerdict: true, nBaseline: 4, nWindow: 1 },
    },
    perDay: {
      before: { visitors: 905, engaged: 0, promoUnits: 214, takeRate: 0.2365 },
      during: { visitors: 1780, engaged: 0, promoUnits: 498, takeRate: 0.2798 },
      after: { visitors: 912, engaged: 0, promoUnits: 221, takeRate: 0.2423 },
    },
    promotedSales: {
      available: true,
      promoUnitsPerDay: { before: 214, after: 498, expected: 214 },
      takeRate: { before: 0.2365, after: 0.2798, deltaPct: 18.3 },
    },
    decomposition: {
      outcome: 'revenue',
      // 905 x 0.2365 x 31.4 = €6,721 a day, against 1780 x 0.2798 x 28.9 = €14,393
      totalPct: 114.2,
      dropped: [],
      // revenue = footfall x purchase rate x basket. NEVER "conversion": that word
      // means buyers over ENGAGED, and the store has no engagement figure at all.
      terms: [
        { term: 'footfall', before: 905, after: 1780, deltaPct: 96.7, contributionPts: 101.4 },
        { term: 'purchase_rate', before: 0.2365, after: 0.2798, deltaPct: 18.3, contributionPts: 25.2 },
        { term: 'basket', before: 31.4, after: 28.9, deltaPct: -8.0, contributionPts: -12.4 },
      ],
    },
    decay: { during: 'sold_more', after: 'normal', archetype: 'snapback', basis: 'sales',
             liftW: 1.327, liftP: 0.033, band: 0.251 },
    economics: {
      basis: 'your_till', items: null, avgPrice: 31.4, avgPricePaid: 28.9,
      unitMarginNormal: 11.93, unitMarginOnPromo: 10.98,
      discountPctMin: 0, discountPctMax: 0, uniformDiscount: true,
    },
    margin: {
      duringEur: 1116,
      afterEur: 233,
      afterKnown: true,
      eur: 1349,
      priceBasis: 'your_till',
      itemCount: null,
      price: 31.4,
      unitMarginNormal: 11.93,
      unitMarginOnPromo: 10.98,
      discountPctMin: 0,
      discountPctMax: 0,
      uniformDiscount: true,
      breakEvenLift: 0.087,
      achievedLift: 1.327,
      clearedBreakEven: true,
      impossible: false,
      fixedCostEur: 1800,
      basis: 'store_margin_pct',
      marginPct: 38,
      confidence: 'good',
    },
    weatherCaveat:
      'A single Saturday leans hard on the weather, and this one was dry and mild — '
      + 'so some of the extra footfall may be the day rather than the gift.',
    daily: {
      engaged: [
        ...series(T.kdBase, 28, 214),
        ...series(T.kdStart, 1, 498),
        ...series(T.kdEnd - 1, 28, 221),
      ],
    },
  },
}

// ---------------------------------------------------------------------------
// 1. Power Tools — nearly tripled the units and still lost money.
// ---------------------------------------------------------------------------

const POWER = {
  PromoId: 1,
  CompanyId: 0,
  StartDate: D(T.powStart),
  EndDate: D(T.powEnd),
  Status: 'active',
  Data: {
    name: 'Dewalt cordless — 2 for 1',
    zoneName: ZONE.power,
    productScope: 'Dewalt cordless drills (3 SKUs)',
    items: [
      { name: 'DCD778 combi drill', price: 69, marginPct: 42, discountPct: 35 },
      { name: 'DCD709 drill', price: 49, marginPct: 46, discountPct: 35 },
      { name: 'DCF885 impact driver', price: 32, marginPct: 48, discountPct: 30 },
    ],
    coverage: 'few_items',
    mechanic: 'bogo',
    advertised: true,
    discountPct: 30,
    fixedCostEur: 250,
    // Units, revenue, and what those goods cost. The cost is what makes the gross
    // margin MEASURED: revenue minus cost, period by period, is where every euro on
    // the result comes from. Note the cost per unit differs between periods — the
    // item mix shifted, which is exactly why we do not assume one.
    salesPeriods: {
      before: { units: 224, revenue: 11200, cost: 6208 },
      during: { units: 336, revenue: 10920, cost: 9129 },
      after: { units: 168, revenue: 8400, cost: 4655 },
    },
    postWindowDays: 28,
    entryMode: 'retrospective',
    notes: 'Ran alongside the regional flyer.',
    outcome: { repeat: 'no', note: '30% was far too deep on drills. A display would do more.' },
  },
  measurement: {
    promoId: 1,
    zone: ZONE.power,
    phase: 'complete',
    advertised: true,
    coverage: COVERAGE.few_items,
    windows: {
      baseline: { start: D(T.powBase), end: D(T.powStart + 1), mode: 'contiguous', days: 28 },
      window: { start: D(T.powStart), end: D(T.powEnd), days: 14 },
      post: { days: 28, target: 28, weeks: 4 },
    },
    excludedDays: 0,
    excludedReasons: {},
    confoundingActions: [],
    // what the STORE did over the same days — reported, never subtracted. There is
    // no cross-zone adjustment: product zones are independent entities.
    storeTraffic: { beforePerDay: 900, duringPerDay: 916, deltaPct: 1.8,
                    mdePct: 11.4, beyondNormal: false },
    detectability: {
      camera: { mdePct: 19.4, metric: 'stops', isVerdict: false, nBaseline: 28, nWindow: 14 },
      sales: { mdePct: 15.7, isVerdict: true, nBaseline: 28, nWindow: 14 },
    },
    perDay: {
      before: { visitors: 100, engaged: 40, promoUnits: 8, takeRate: 0.2 },
      during: { visitors: 108, engaged: 46, promoUnits: 24, takeRate: 0.5217 },
      after: { visitors: 98, engaged: 38, promoUnits: 6, takeRate: 0.1579 },
    },
    promotedSales: {
      available: true,
      trendBasis: 'control_zones',
      trendRatio: 1.03,
      promoUnitsPerDay: { before: 8, after: 24, expected: 8 },
      takeRate: { before: 0.2, after: 0.5217, deltaPct: 160.9 },
    },
    decomposition: {
      outcome: 'revenue',
      totalPct: 200.6,
      dropped: [],
      terms: [
        { term: 'footfall', before: 900, after: 916, deltaPct: 1.8, contributionPts: 3.2 },
        { term: 'reach', before: 0.111, after: 0.118, deltaPct: 6.3, contributionPts: 11.1 },
        { term: 'engagement', before: 0.4, after: 0.426, deltaPct: 6.5, contributionPts: 11.5 },
        { term: 'conversion', before: 0.2, after: 0.5217, deltaPct: 160.9, contributionPts: 174.8 },
        { term: 'basket', before: 50, after: 50, deltaPct: 0, contributionPts: 0 },
      ],
    },
    decay: { during: 'sold_more', after: 'lower', archetype: 'payback', basis: 'sales',
             liftW: 2.0, liftP: -0.25, band: 0.157 },
    economics: {
      basis: 'your_items', items: 3, avgPrice: 50,
      unitMarginNormal: 22.29, unitMarginOnPromo: 5.33,
      discountPctMin: 30, discountPctMax: 35, uniformDiscount: false,
    },
    margin: {
      duringEur: -955,
      afterEur: -1247,
      afterKnown: true,
      eur: -2202,
      priceBasis: 'your_items',
      itemCount: 3,
      price: 50,
      unitMarginNormal: 22.29,
      unitMarginOnPromo: 5.33,
      discountPctMin: 30,
      discountPctMax: 35,
      uniformDiscount: false,
      breakEvenLift: 3.182,
      achievedLift: 2.0,
      clearedBreakEven: false,
      impossible: false,
      fixedCostEur: 250,
      // cost of goods was given for every period, so this is revenue minus cost —
      // not price x margin x discount as typed from memory
      basis: 'till',
      confidence: 'good',
    },
    weatherCaveat: null,
    daily: {
      engaged: [
        ...series(T.powBase, 28, 40),
        ...series(T.powStart, 14, 46),
        ...series(T.powEnd - 1, 28, 38),
      ],
    },
  },
}

// ---------------------------------------------------------------------------
// 2. Garden Utilities — no discount at all, and the lift outlived the promo.
// ---------------------------------------------------------------------------

const GARDEN = {
  PromoId: 2,
  CompanyId: 0,
  StartDate: D(T.garStart),
  EndDate: D(T.garEnd),
  Status: 'concluded',
  Data: {
    name: 'Spring garden feature display',
    zoneName: ZONE.garden,
    productScope: 'Whole spring garden range, on a feature display',
    items: [
      { name: 'Spring bulb sets', price: 12.5, marginPct: 48, discountPct: 0 },
      { name: 'Potting compost 40L', price: 8.99, marginPct: 35, discountPct: 0 },
      { name: 'Hand tool set', price: 24.95, marginPct: 44, discountPct: 0 },
    ],
    coverage: 'most_of_aisle',
    mechanic: 'display_move',
    advertised: false,
    discountPct: 0,
    fixedCostEur: 480,
    salesPeriods: {
      before: { units: 364, revenue: 5635, cost: 3192 },
      during: { units: 441, revenue: 6827, cost: 3868 },
      after: { units: 532, revenue: 8235, cost: 4665 },
    },
    postWindowDays: 28,
    entryMode: 'retrospective',
    outcome: { repeat: 'as_was', note: 'Worth doing again next spring — the display did it, not a price.' },
  },
  measurement: {
    promoId: 2,
    zone: ZONE.garden,
    phase: 'complete',
    advertised: false,
    coverage: COVERAGE.most_of_aisle,
    windows: {
      baseline: { start: D(T.garBase), end: D(T.garStart + 1), mode: 'contiguous', days: 28 },
      window: { start: D(T.garStart), end: D(T.garEnd), days: 21 },
      post: { days: 28, target: 28, weeks: 4 },
    },
    excludedDays: 0,
    excludedReasons: {},
    confoundingActions: [],
    storeTraffic: { beforePerDay: 880, duringPerDay: 902, deltaPct: 2.5,
                    mdePct: 9.8, beyondNormal: false },
    detectability: {
      camera: { mdePct: 16.7, metric: 'stops', isVerdict: true, nBaseline: 28, nWindow: 21 },
      sales: { mdePct: 13.3, isVerdict: true, nBaseline: 28, nWindow: 21 },
    },
    perDay: {
      before: { visitors: 110, engaged: 55, promoUnits: 13, takeRate: 0.2364 },
      during: { visitors: 137, engaged: 86, promoUnits: 21, takeRate: 0.245 },
      after: { visitors: 126, engaged: 78, promoUnits: 19, takeRate: 0.2436 },
    },
    promotedSales: {
      available: true,
      trendBasis: 'control_zones',
      trendRatio: 1.031,
      promoUnitsPerDay: { before: 13, after: 21, expected: 13 },
      takeRate: { before: 0.2364, after: 0.245, deltaPct: 3.6 },
    },
    decomposition: {
      outcome: 'revenue',
      // both reach and the stopping share clear the 17.8% bar — the only promo here
      // big enough for the footfall to carry a conclusion on its own
      totalPct: 61.5,
      dropped: [],
      terms: [
        { term: 'footfall', before: 880, after: 902, deltaPct: 2.5, contributionPts: 3.2 },
        { term: 'reach', before: 0.125, after: 0.152, deltaPct: 21.6, contributionPts: 25.1 },
        { term: 'engagement', before: 0.5, after: 0.625, deltaPct: 25.0, contributionPts: 28.6 },
        { term: 'conversion', before: 0.2364, after: 0.245, deltaPct: 3.6, contributionPts: 4.6 },
        { term: 'basket', before: 31, after: 31, deltaPct: 0, contributionPts: 0 },
      ],
    },
    decay: { during: 'sold_more', after: 'higher', archetype: 'sticky', basis: 'sales',
             liftW: 0.615, liftP: 0.462, band: 0.167 },
    economics: {
      basis: 'your_items', items: 3, avgPrice: 15.48,
      unitMarginNormal: 6.71, unitMarginOnPromo: 6.71,
      discountPctMin: 0, discountPctMax: 0, uniformDiscount: true,
    },
    margin: {
      duringEur: 647,
      afterEur: 1127,
      afterKnown: true,
      eur: 1774,
      priceBasis: 'your_items',
      itemCount: 3,
      price: 15.48,
      unitMarginNormal: 6.71,
      unitMarginOnPromo: 6.71,
      discountPctMin: 0,
      discountPctMax: 0,
      uniformDiscount: true,
      breakEvenLift: 0,
      achievedLift: 0.615,
      clearedBreakEven: true,
      impossible: false,
      fixedCostEur: 480,
      // cost of goods was given for every period, so this is revenue minus cost —
      // not price x margin x discount as typed from memory
      basis: 'till',
      confidence: 'good',
    },
    weatherCaveat:
      'Garden Utilities leans on warm weather, and this ran through an unusually warm '
      + 'stretch — so some of the gain may be the weather, not the change.',
    daily: {
      engaged: [
        ...series(T.garBase, 28, 55),
        ...series(T.garStart, 21, 85.7),
        ...series(T.garEnd - 1, 28, 78),
      ],
    },
  },
}

// ---------------------------------------------------------------------------
// 3. Safety Shoes — sold more, didn't cover the discount, back to normal.
// ---------------------------------------------------------------------------

const SAFETY = {
  PromoId: 3,
  CompanyId: 0,
  StartDate: D(T.safStart),
  EndDate: D(T.safEnd),
  Status: 'concluded',
  Data: {
    name: 'Safety Week −20%',
    zoneName: ZONE.safety,
    productScope: 'S3 work boots',
    items: [
      { name: 'S3 work boot, black', price: 79.95, marginPct: 38, discountPct: 20 },
      { name: 'S3 work boot, brown', price: 64.95, marginPct: 36, discountPct: 20 },
    ],
    coverage: 'section',
    mechanic: 'pct_off',
    advertised: true,
    discountPct: 20,
    fixedCostEur: 120,
    salesPeriods: {
      // 7,655 not 7,656 on purpose: at 7,656 the subtraction lands on exactly
      // −555.50, which Python rounds to −556 and JavaScript to −555. A figure that
      // differs between the engine and anything re-deriving it is a latent parity bug,
      // so the demo stays off the half-cent.
      before: { units: 168, revenue: 12171, cost: 7655 },
      during: { units: 95, revenue: 5507, cost: 4330 },
      // the point of this one: the discount barely washed its face while it ran, and
      // the buyers it brought came back over the next four weeks at FULL price
      after: { units: 250, revenue: 18112, cost: 11392 },
    },
    postWindowDays: 28,
    entryMode: 'retrospective',

  },
  measurement: {
    promoId: 3,
    zone: ZONE.safety,
    phase: 'complete',
    advertised: true,
    coverage: COVERAGE.section,
    windows: {
      baseline: { start: D(T.safBase), end: D(T.safStart + 1), mode: 'contiguous', days: 28 },
      window: { start: D(T.safStart), end: D(T.safEnd), days: 10 },
      post: { days: 28, target: 28, weeks: 4 },
    },
    excludedDays: 0,
    excludedReasons: {},
    confoundingActions: [
      { actionId: 7, what: 'Moved the fitting stool to the aisle head',
        date: D(T.safBase - 10), where: 'baseline' },
    ],
    storeTraffic: { beforePerDay: 890, duringPerDay: 894, deltaPct: 0.4,
                    mdePct: 13.1, beyondNormal: false },
    detectability: {
      // stopping went 35 -> 47 a day, +34% against a 23% band: a real camera move.
      // This read `false` while the camera verdict was taken from a cross-zone
      // difference-in-differences figure, which is now gone.
      camera: { mdePct: 23.1, metric: 'stops', isVerdict: true, nBaseline: 28, nWindow: 10 },
      sales: { mdePct: 18.3, isVerdict: true, nBaseline: 28, nWindow: 10 },
    },
    perDay: {
      before: { visitors: 87, engaged: 35, promoUnits: 6, takeRate: 0.1711 },
      during: { visitors: 114, engaged: 47, promoUnits: 9.5, takeRate: 0.2026 },
      after: { visitors: 101, engaged: 42, promoUnits: 8.9, takeRate: 0.2119 },
    },
    promotedSales: {
      available: true,
      trendBasis: 'control_zones',
      trendRatio: 1.024,
      promoUnitsPerDay: { before: 6, after: 9.5, expected: 6 },
      takeRate: { before: 0.1711, after: 0.2026, deltaPct: 18.4 },
    },
    decomposition: {
      outcome: 'revenue',
      // reach clears the 26.9% bar, the stopping share does not: they came and
      // walked past. The one footfall read the cameras can genuinely carry here.
      totalPct: 58.3,
      dropped: [],
      terms: [
        { term: 'footfall', before: 890, after: 894, deltaPct: 0.4, contributionPts: 0.6 },
        { term: 'reach', before: 0.098, after: 0.128, deltaPct: 30.6, contributionPts: 33.8 },
        { term: 'engagement', before: 0.402, after: 0.41, deltaPct: 2.0, contributionPts: 2.5 },
        { term: 'conversion', before: 0.1711, after: 0.2026, deltaPct: 18.4, contributionPts: 21.4 },
        { term: 'basket', before: 68, after: 68, deltaPct: 0, contributionPts: 0 },
      ],
    },
    // 6 a day before, 9.5 while it ran, 8.9 for the four weeks after: the lasting
    // half is the bigger half, which is the whole reason the after-window exists
    decay: { during: 'sold_more', after: 'higher', archetype: 'sticky', basis: 'sales',
             liftW: 0.583, liftP: 0.488, band: 0.231 },
    economics: {
      basis: 'your_items', items: 2, avgPrice: 72.45,
      unitMarginNormal: 26.88, unitMarginOnPromo: 12.39,
      discountPctMin: 20, discountPctMax: 20, uniformDiscount: true,
    },
    margin: {
      duringEur: -556,
      afterEur: 2204,
      afterKnown: true,
      eur: 1648,
      priceBasis: 'your_items',
      itemCount: 2,
      price: 72.45,
      unitMarginNormal: 26.88,
      unitMarginOnPromo: 12.39,
      discountPctMin: 20,
      discountPctMax: 20,
      uniformDiscount: true,
      breakEvenLift: 1.169,
      achievedLift: 0.583,
      clearedBreakEven: false,
      impossible: false,
      fixedCostEur: 120,
      // cost of goods was given for every period, so this is revenue minus cost —
      // not price x margin x discount as typed from memory
      basis: 'till',
      confidence: 'good',
    },
    weatherCaveat: null,
    daily: {
      engaged: [
        ...series(T.safBase, 28, 35),
        ...series(T.safStart, 10, 46.9),
        ...series(T.safEnd - 1, 28, 42),
      ],
    },
  },
}

// ---------------------------------------------------------------------------
// 4. Promoted Articles Zone — running right now, nothing resolvable yet.
// ---------------------------------------------------------------------------

const RUNNING = {
  PromoId: 4,
  CompanyId: 0,
  StartDate: D(T.runStart),
  EndDate: D(T.runEnd),
  Status: 'active',
  Data: {
    name: 'Back-to-work bundle',
    zoneName: ZONE.promoted,
    productScope: 'Workwear + toolbelt bundle',
    items: [
      { name: 'Workwear trousers', price: 44.95, marginPct: 41, discountPct: 15 },
      { name: 'Toolbelt', price: 34.95, marginPct: 39, discountPct: 15 },
    ],
    coverage: 'most_of_aisle',
    mechanic: 'bundle',
    advertised: false,
    discountPct: 15,
    fixedCostEur: 200,
    postWindowDays: 28,
    entryMode: 'prospective',
    expectedLever: 'engagement',
  },
  measurement: {
    promoId: 4,
    zone: ZONE.promoted,
    phase: 'running',
    advertised: false,
    coverage: COVERAGE.most_of_aisle,
    windows: {
      baseline: { start: D(T.runBase), end: D(T.runStart + 1), mode: 'contiguous', days: 28 },
      window: { start: D(T.runStart), end: D(T.runEnd), days: 4 },
      post: { days: 0, target: 28, weeks: 4 },
    },
    excludedDays: 0,
    excludedReasons: {},
    confoundingActions: [],
    storeTraffic: { beforePerDay: 905, duringPerDay: 918, deltaPct: 1.4,
                    mdePct: 18.7, beyondNormal: false },
    detectability: {
      camera: { mdePct: 36.0, metric: 'zone_traffic', isVerdict: false, nBaseline: 28, nWindow: 4 },
      sales: null,
    },
    perDay: {
      before: { visitors: 81, engaged: 40, promoUnits: null, takeRate: null },
      during: { visitors: 85, engaged: 46, promoUnits: null, takeRate: null },
    },
    promotedSales: { available: false, reason: 'no_promo_sales' },
    decomposition: {
      outcome: 'engaged',
      totalPct: 7.7,
      dropped: ['conversion', 'basket'],
      terms: [
        { term: 'footfall', before: 905, after: 918, deltaPct: 1.4, contributionPts: 1.5 },
        { term: 'reach', before: 0.09, after: 0.093, deltaPct: 3.3, contributionPts: 3.4 },
        { term: 'engagement', before: 0.438, after: 0.45, deltaPct: 2.7, contributionPts: 2.8 },
      ],
    },
    decay: { during: 'no_change', after: null, archetype: 'no_effect', basis: 'traffic',
             liftW: null, liftP: null, band: 0.382 },
    economics: null,
    margin: { confidence: 'na', reason: 'no_sales' },
    weatherCaveat: null,
    daily: {
      engaged: [...series(T.runBase, 28, 40), ...series(T.runStart, 4, 46)],
    },
  },
  live: {
    alarms: [
      {
        kind: 'promo_unserved',
        severity: 'warning',
        title: 'Demand you are not serving',
        text: 'People are stopping at the bundle more than usual, and 11 of them yesterday '
          + 'stayed long enough to want help and got nobody. Put someone in that aisle today.',
      },
      {
        kind: 'promo_queue',
        severity: 'warning',
        title: 'The queue is eating the promo',
        text: 'Checkout wait hit 6m10s yesterday against your own best of 3m40s. A busier '
          + 'aisle only pays if the till keeps up.',
      },
    ],
  },
}

// ---------------------------------------------------------------------------
// 5. A finished weekend promo with no sales figures — the ordinary case.
// ---------------------------------------------------------------------------

const WEEKEND = {
  PromoId: 5,
  CompanyId: 0,
  StartDate: D(T.weekStart),
  EndDate: D(T.weekEnd),
  Status: 'active',
  Data: {
    name: 'Weekend of the pro tools',
    zoneName: ZONE.promoted,
    productScope: 'Pro tool bundle',
    coverage: 'most_of_aisle',
    items: [
      { name: 'Pro tool bundle', price: 119, marginPct: 38, discountPct: 20 },
    ],
    mechanic: 'pct_off',
    advertised: true,
    discountPct: 20,
    fixedCostEur: 160,
    postWindowDays: 28,
    entryMode: 'retrospective',
  },
  measurement: {
    promoId: 5,
    zone: ZONE.promoted,
    phase: 'complete',
    coverage: COVERAGE.most_of_aisle,
    windows: {
      baseline: { start: D(T.weekBase), end: D(T.weekStart + 1), mode: 'matched-weekday', days: 8 },
      window: { start: D(T.weekStart), end: D(T.weekEnd), days: 2 },
      post: { days: 8, target: 28, weeks: 4 },
    },
    excludedDays: 0,
    excludedReasons: {},
    confoundingActions: [],
    storeTraffic: { beforePerDay: 930, duringPerDay: 948, deltaPct: 1.9,
                    mdePct: 14.2, beyondNormal: false },
    detectability: {
      camera: { mdePct: 19.6, metric: 'zone_traffic', isVerdict: true, nBaseline: 8, nWindow: 2 },
      sales: null,
    },
    // no figures: the money cannot be computed, and we must not invent it
    promotedSales: { available: false, reason: 'no_promo_sales' },
    economics: null,
    margin: { confidence: 'na', reason: 'no_sales' },
    perDay: {
      before: { visitors: 148, engaged: 71, promoUnits: null, takeRate: null },
      during: { visitors: 193, engaged: 104, promoUnits: null, takeRate: null },
      after: { visitors: 151, engaged: 73, promoUnits: null, takeRate: null },
    },
    decomposition: {
      outcome: 'engaged',
      totalPct: 46.5,
      dropped: ['conversion', 'basket'],
      terms: [
        { term: 'footfall', before: 930, after: 948, deltaPct: 1.9, contributionPts: 1.9 },
        { term: 'reach', before: 0.159, after: 0.204, deltaPct: 28.3, contributionPts: 28.9 },
        { term: 'engagement', before: 0.48, after: 0.539, deltaPct: 12.3, contributionPts: 15.7 },
      ],
    },
    // no sales figures were ever entered for this one, so the verdict is measured on
    // ZONE TRAFFIC — during and after, one metric — and every word follows that
    // 148 -> 193 -> 151 visitors a day against this zone's own baseline: +30%, then flat
    decay: { during: 'sold_more', after: 'normal', archetype: 'snapback', basis: 'traffic',
             liftW: 0.304, liftP: 0.02, band: 0.196 },
    weatherCaveat: null,
    daily: {
      engaged: [
        ...series(T.weekBase, 8, 71),
        ...series(T.weekStart, 2, 104),
        ...series(T.weekEnd - 1, 8, 73),
      ],
    },
  },
}

export const DEMO_PROMOS = [RUNNING, JUST_FINISHED, WEEKEND, CUSTOMER_DAY,
                            POWER, SAFETY, GARDEN]

// ---------------------------------------------------------------------------
// Dutch alongside, per PromoId: each record's descriptive text in the portal's current
// language (withNl). Promo, product and zone names stay as they are; every figure
// matches the English.
// ---------------------------------------------------------------------------
const NL_TEXT = {
  7: { Data: { productScope: 'Gevoerde S3-winterschoenen' } },
  6: {
    Data: { productScope: 'Een klein cadeau voor elke klant' },
    outcome: { note: 'De wachtrij aan de kassa was het enige probleem.' },
    measurement: {
      weatherCaveat: 'Eén zaterdag hangt sterk af van het weer, en deze was droog en zacht — '
        + 'dus een deel van de extra winkelbezoekers kan aan de dag liggen in plaats van aan het cadeau.',
    },
  },
  1: {
    Data: { productScope: 'Dewalt snoerloze boormachines (3 artikelen)',
            notes: 'Liep gelijk met de regionale folder.' },
    outcome: { note: '30% was veel te diep voor boormachines. Een display zou meer doen.' },
  },
  2: {
    Data: { productScope: 'Het hele voorjaarsassortiment voor de tuin, op een opvallende display' },
    outcome: { note: 'Volgend voorjaar zeker opnieuw — de display deed het, niet een prijs.' },
    measurement: {
      weatherCaveat: 'Garden Utilities leunt sterk op warm weer, en deze periode was ongewoon warm '
        + '— dus een deel van de winst kan aan het weer liggen, niet aan de wijziging.',
    },
  },
  3: {
    Data: { productScope: 'S3-werkschoenen' },
    confounding: [{ what: 'Het pasbankje naar de kop van het gangpad verplaatst' }],
  },
  4: {
    Data: { productScope: 'Combi-aanbod werkkleding + gereedschapsgordel' },
    alarms: [
      { title: 'Bezoekers die niemand helpt',
        text: 'Meer mensen dan normaal blijven staan bij het combi-aanbod, en 11 van hen bleven gisteren '
          + 'lang genoeg om hulp te willen en kregen niemand. Zet vandaag iemand in dat gangpad.' },
      { title: 'De wachtrij eet de promo op',
        text: 'De wachttijd aan de kassa liep gisteren op tot 6 min 10 sec, tegenover je eigen beste van '
          + '3 min 40 sec. Een drukker gangpad loont alleen als de kassa het bijhoudt.' },
    ],
  },
  5: { Data: { productScope: 'Combi-aanbod profgereedschap' } },
}
for (const p of DEMO_PROMOS) {
  const nl = NL_TEXT[p.PromoId]
  if (!nl) continue
  if (nl.Data) withNl(p.Data, nl.Data)
  if (nl.outcome && p.Data.outcome) withNl(p.Data.outcome, nl.outcome)
  if (nl.measurement) withNl(p.measurement, nl.measurement)
  ;(nl.confounding || []).forEach((t, i) => withNl(p.measurement.confoundingActions[i], t))
  ;(nl.alarms || []).forEach((t, i) => withNl(p.live.alarms[i], t))
}

// §9 item 5: a promo keeps its result once its after-period is complete — as the
// backend serves it: frozen, measured until the day after the after-period ended.
for (const p of DEMO_PROMOS) {
  if (p.measurement.phase !== 'complete') continue
  const done = new Date(`${p.EndDate}T00:00:00Z`)
  done.setUTCDate(done.getUTCDate() + (p.Data.postWindowDays || 28) + 1)
  p.measurement.frozen = true
  p.measurement.measuredUntil = done.toISOString().slice(0, 10)
}

export const DEMO_PROMO_ZONES = [ZONE.power, ZONE.garden, ZONE.safety, ZONE.promoted]

/**
 * The product groups this store records daily sales under — the hand-typed feed that
 * stands in for a till connection. Each carries how much has actually been entered,
 * because "pick a group" means nothing until you can see whether that group has any
 * numbers in it. `workwear-bundle` deliberately has none, to show that state.
 */
export const DEMO_SALES_GROUPS = [
  withNl({ category: 'dewalt-cordless', label: 'Dewalt cordless', days: 182, lastEntry: D(1) },
    { label: 'Dewalt snoerloos' }),
  withNl({ category: 'garden-spring', label: 'Spring garden range', days: 210, lastEntry: D(1) },
    { label: 'Voorjaarsassortiment tuin' }),
  withNl({ category: 's3-boots', label: 'S3 work boots', days: 95, lastEntry: D(3) },
    { label: 'S3-werkschoenen' }),
  withNl({ category: 'workwear-bundle', label: 'Workwear bundle', days: 0, lastEntry: null },
    { label: 'Combi-aanbod werkkleding' }),
]

export function demoSalesGroup(category) {
  return DEMO_SALES_GROUPS.find((g) => g.category === category) || null
}

export const DEMO_COVERAGE_OPTIONS = [
  withNl({ key: 'few_items', label: 'A few items' }, { label: 'Een paar artikelen' }),
  withNl({ key: 'section', label: 'A shelf or brand block' }, { label: 'Een schap of merkblok' }),
  withNl({ key: 'most_of_aisle', label: 'Most of the zone' }, { label: 'Het grootste deel van de zone' }),
]

/**
 * The most recent finished promo in a zone that carries a judgement — what the log
 * form shows when you pick that zone again.
 */
export function demoPriorPromo(zone, excludeId = null) {
  return DEMO_PROMOS
    .filter((p) => p.Data.zoneName === zone
      && p.PromoId !== excludeId
      && p.Data.outcome?.repeat
      && p.measurement.phase === 'complete')
    .sort((a, b) => (a.EndDate < b.EndDate ? 1 : -1))[0] || null
}

export function demoPromoById(id) {
  return DEMO_PROMOS.find((p) => String(p.PromoId) === String(id)) || null
}

/**
 * Readiness for the form — what this promo will and will not be able to prove,
 * before the dates are committed. Mirrors coach/promo.readiness.
 */
export function demoReadiness({ zone, start, end, coverage = 'few_items',
                                economics = null, hasSales = true }) {
  const days = Math.max(1, Math.round((new Date(end) - new Date(start)) / 86400000) + 1)
  // same rule as the engine: 4 same-weekday blocks up to a week, otherwise four
  // weeks or the promo rounded up to whole weeks, whichever is longer
  const baselineDays = days <= 7 ? days * 4 : Math.max(28, Math.ceil(days / 7) * 7)
  // 2.8 * sigma * sqrt(1/n_w + 1/n_b), scaled off a known point so the readiness
  // figure moves with the window lengths the way the real one does
  const cameraMde = Math.round(
    19.4 * Math.sqrt((1 / days + 1 / baselineDays) / (1 / 14 + 1 / 28)) * 10) / 10
  const salesMde = Math.round(cameraMde * 0.81 * 10) / 10
  // the break-even comes from the same per-item money the result will use, so the
  // form and the verdict can never disagree
  const e = economics
  const breakEven = e && e.unitMarginOnPromo > 0
    ? Math.round((e.unitMarginNormal / e.unitMarginOnPromo - 1) * 1000) / 1000
    : null
  const cov = COVERAGE[coverage] || null

  return {
    zone,
    baseline: { start, end, mode: days <= 7 ? 'matched-weekday' : 'contiguous', days: baselineDays },
    windowDays: days,
    coverage: cov,
    detectability: { camera: { mdePct: cameraMde }, sales: hasSales ? { mdePct: salesMde } : null },
    breakEvenLift: breakEven,
    economics: e,
    unitMarginNormal: e ? e.unitMarginNormal : null,
    unitMarginOnPromo: e ? e.unitMarginOnPromo : null,
    risks: [
      ...(e && e.unitMarginOnPromo <= 0
        ? [withNl({ kind: 'no_break_even',
             text: 'At these discounts you lose money on every sale, however many you sell.' },
           { text: 'Met deze kortingen verlies je geld op elke verkoop, hoeveel je er ook verkoopt.' })]
        : []),
      ...(!hasSales
        ? [withNl({ kind: 'no_sales',
             text: 'No sales figures — you will get whether the promo was noticed, but not '
               + 'whether it sold or what it made.' },
           { text: 'Geen verkoopcijfers — je ziet wel of de promo opgemerkt werd, maar niet '
               + 'of er meer verkocht werd of wat het opbracht.' })]
        : []),
      // the coverage's own warning, read in the current language
      ...(cov && cov.warning
        ? [Object.defineProperty({ kind: 'small_coverage' }, 'text',
            { get: () => cov.warning, enumerable: true })]
        : []),
    ],
    controls: DEMO_PROMO_ZONES.filter((z) => z !== zone),
  }
}
